import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { Page } from '@/shared/ui/Page';
import { PageHeader } from '@/shared/ui/PageHeader';
import { GreenLiquidGlassButton } from '@/shared/ui/GreenLiquidGlassButton';
import { paymentApi } from '@/entities/payment-claim/api';
import { useT } from '@/shared/lib/i18n';

const MAX_BYTES = 10 * 1024 * 1024;

export function AttachReceipt() {
  const { claimId } = useParams<{ claimId: string }>();
  const t = useT();
  const [file, setFile] = useState<File | null>(null);
  const [tooLarge, setTooLarge] = useState(false);

  const mutation = useMutation({
    mutationFn: () => paymentApi.uploadReceipt(claimId!, file!),
  });

  const handleFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    const picked = event.target.files?.[0] ?? null;
    if (picked && picked.size > MAX_BYTES) {
      setTooLarge(true);
      setFile(null);
      return;
    }
    setTooLarge(false);
    setFile(picked);
  };

  return (
    <Page>
      <PageHeader title={<span className="font-bold text-[19px]">{t('attachReceipt.title')}</span>} />
      <div className="px-[18px] pb-5 flex flex-col gap-4 min-h-0">
        <p className="text-[14px] text-text-secondary">{t('attachReceipt.hint')}</p>

        <label className="rounded-[14px] px-4 py-6 text-center bg-white/55 border border-dashed border-white/70 cursor-pointer">
          <input type="file" accept="image/*,.pdf" className="hidden" onChange={handleFile} />
          <span className="text-[15px] font-semibold text-brand-green">
            {file ? file.name : t('attachReceipt.pick')}
          </span>
        </label>

        {tooLarge && (
          <p className="text-[13px] text-status-debt text-center">{t('attachReceipt.tooLarge')}</p>
        )}

        <div className="mt-auto">
          {mutation.isError && (
            <p className="text-[13px] text-status-debt text-center mb-2">{t('attachReceipt.error')}</p>
          )}
          {mutation.isSuccess && (
            <p className="text-[13px] text-status-paid text-center mb-2">{t('attachReceipt.success')}</p>
          )}
          <GreenLiquidGlassButton
            disabled={!file || mutation.isSuccess}
            loading={mutation.isPending}
            onClick={() => mutation.mutate()}
          >
            {t('attachReceipt.submit')}
          </GreenLiquidGlassButton>
        </div>
      </div>
    </Page>
  );
}
